// ================================================================
// i18n — چندزبانگی
// ================================================================
// استفاده:
//   import { t } from './i18n/index.js';
//   t('sidebar.scenarios')  →  'Scenarios' یا 'Szenarien' بسته به زبان

import { events, EVENTS } from '../core/events.js';

// زبان‌ها LAZY لود می‌شن (dynamic import): با ۱۷ زبان، import مستقیم
// باندل اصلی رو ۳ برابر می‌کرد (۹۲۸KB→۲.۲MB). Vite هر فایل رو chunk جدا
// می‌کنه و فقط زبان انتخاب‌شده دانلود می‌شه.
// زبان جدید: (1) در languages.js ثبت، (2) <code>.json با
// scripts/translate-i18n.mjs بساز، (3) اینجا در LOADERS اضافه کن.
import { APP_LANGUAGES } from './languages.js';

const LOADERS = {
  de: () => import('./de.json'),
  en: () => import('./en.json'),
  fr: () => import('./fr.json'),
  it: () => import('./it.json'),
  es: () => import('./es.json'),
  pt: () => import('./pt.json'),
  tr: () => import('./tr.json'),
  sq: () => import('./sq.json'),
  sr: () => import('./sr.json'),
  hr: () => import('./hr.json'),
  ar: () => import('./ar.json'),
  ru: () => import('./ru.json'),
  uk: () => import('./uk.json'),
  pl: () => import('./pl.json'),
  nl: () => import('./nl.json'),
  fa: () => import('./fa.json'),
  ku: () => import('./ku.json'),
};

// متادیتای زبان‌ها (پرچم/نام) از رجیستری واحد languages.js میاد
const LANGUAGES = Object.fromEntries(
  APP_LANGUAGES.filter((l) => LOADERS[l.code]).map((l) => [
    l.code,
    { label: l.code.toUpperCase(), name: l.name },
  ])
);

// کش ترجمه‌های لودشده — هر زبان فقط یک بار دانلود می‌شه
const loaded = {};
async function loadTranslations(lang) {
  if (!loaded[lang]) loaded[lang] = (await LOADERS[lang]()).default;
  return loaded[lang];
}

const AVAILABLE_LANGUAGES = Object.keys(LANGUAGES);
const STORAGE_KEY = 'make-clone:language';

let currentLanguage = 'en';
let translations = {};

/**
 * بارگذاری زبان
 * اولویت: زبان ذخیره شده در localStorage → زبان پارامتر → en
 */
export async function initI18n(defaultLang = 'en') {
  // اول از localStorage بخون
  let lang = defaultLang;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && AVAILABLE_LANGUAGES.includes(saved)) {
      lang = saved;
    }
  } catch (e) {
    // localStorage ممکنه در private mode کار نکنه
  }

  if (!AVAILABLE_LANGUAGES.includes(lang)) {
    lang = 'en';
  }

  translations = await loadTranslations(lang);
  currentLanguage = lang;

  document.documentElement.lang = lang;
  events.emit(EVENTS.LANGUAGE_CHANGED, lang);
}

/**
 * تغییر زبان — ذخیره در localStorage هم می‌شه
 */
export async function setLanguage(lang) {
  if (!AVAILABLE_LANGUAGES.includes(lang)) return;
  if (lang === currentLanguage) return;

  // اول لود، بعد سوییچ — تا لحظه‌ای وجود نداشته باشه که t() ترجمهٔ اشتباه بده
  translations = await loadTranslations(lang);
  currentLanguage = lang;

  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch (e) {}

  document.documentElement.lang = lang;
  events.emit(EVENTS.LANGUAGE_CHANGED, lang);
}

/**
 * دریافت زبان فعلی
 */
export function getLanguage() {
  return currentLanguage;
}

/**
 * جست‌وجوی داخلی — به‌جای warn، undefined برمی‌گرداند
 */
function lookup(key) {
  const parts = key.split('.');
  let value = translations;
  for (const part of parts) {
    if (value && typeof value === 'object' && part in value) {
      value = value[part];
    } else {
      return undefined;
    }
  }
  return value;
}

/**
 * تابع ترجمه
 *
 * Plural: wird `count` übergeben, sucht t() zuerst die sprachrichtige Kategorie
 * (`<key>_one`, `<key>_other`, …) und fällt sonst auf `<key>` zurück. Welche
 * Kategorien es gibt, weiss Intl.PluralRules je Sprache — Deutsch/Englisch kennen
 * one/other, andere Sprachen mehr (z.B. few/many). Darum NICHT selbst `n === 1`
 * prüfen: das ist eine Regel der Sprache, nicht des Codes.
 *
 * Ohne `count` verhält sich t() unverändert.
 */
export function t(key, params = {}) {
  let value;

  if (typeof params.count === 'number') {
    const category = new Intl.PluralRules(currentLanguage).select(params.count);
    value = lookup(`${key}_${category}`);
    if (value === undefined) value = lookup(`${key}_other`);
  }
  if (value === undefined) value = lookup(key);

  if (value === undefined) {
    console.warn(`Missing translation: ${key}`);
    return key;
  }

  if (typeof value === 'string' && Object.keys(params).length > 0) {
    return value.replace(/\{(\w+)\}/g, (_, k) => params[k] ?? `{${k}}`);
  }

  return value;
}

/**
 * لیست زبان‌های موجود (با metadata)
 */
export function getAvailableLanguages() {
  return AVAILABLE_LANGUAGES.map(code => ({
    code,
    label: LANGUAGES[code].label,
    name: LANGUAGES[code].name
  }));
}
